/**
 * ConnectionStatusContext — global "network quality" signal that
 * drifts between good / warn / bad over time.
 *
 * The provider owns a single setInterval that updates the status
 * every ~5–9 seconds. Components subscribe via `useConnectionStatus()`
 * — only those components re-render on status change, so the heavy
 * SVG NodeOrbit is not affected (it doesn't subscribe).
 *
 * Distribution:
 *   88% good — green dot, slow pulse
 *    9% warn — orange dot, faster pulse
 *    3% bad  — red dot, urgent pulse
 *
 * When status is `warn` or `bad`, the TerminalLog uses this signal
 * to bias its log generation toward more WARN/ERR PING & NET messages
 * so the user sees the explanation alongside the indicator dot.
 *
 * ⚠ TODO (server-driven): the status is currently derived from a
 *   local Math.random() — different users will see different colours
 *   at the same time. Move to a backend feed so all users see the
 *   same network state. See memory/feedback_luna_server_random.md
 */
import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import { currentTick, syncedRandom } from '@/lib/sharedRandom';

export type ConnectionQuality = 'good' | 'warn' | 'bad';

const ConnectionStatusContext = createContext<ConnectionQuality>('good');

interface ProviderProps {
  children: ReactNode;
}

export function ConnectionStatusProvider({ children }: ProviderProps) {
  const [status, setStatus] = useState<ConnectionQuality>('good');

  useEffect(() => {
    let cancelled = false;
    let timeoutId: number | undefined;

    // Server-driven синхрон: все клиенты в одном 6-сек окне видят
    // одинаковый статус (good/warn/bad). 6с = 88%/9%/3% раскладка,
    // вычисляется детерминированно из Date.now().
    const compute = (): ConnectionQuality => {
      const tickId = currentTick(6000);
      const r = syncedRandom(tickId, 30);
      if (r < 0.88) return 'good';
      if (r < 0.97) return 'warn';
      return 'bad';
    };

    const tick = () => {
      if (cancelled) return;
      setStatus(compute());
      const nextAt = (currentTick(6000) + 1) * 6000;
      timeoutId = window.setTimeout(tick, Math.max(500, nextAt - Date.now()));
    };

    // Стартуем сразу с синхронного значения
    setStatus(compute());
    timeoutId = window.setTimeout(tick, 6000);
    return () => {
      cancelled = true;
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, []);

  return (
    <ConnectionStatusContext.Provider value={status}>
      {children}
    </ConnectionStatusContext.Provider>
  );
}

export function useConnectionStatus(): ConnectionQuality {
  return useContext(ConnectionStatusContext);
}
